import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Alert, Anchor, Badge, Center, Code, Group, Loader, Pagination, Paper, Skeleton as MSkeleton, Stack, Table,
  Text, Tooltip,
} from '@mantine/core'
import {
  IconAlertCircle, IconAlertTriangle, IconInfoCircle, IconPlugConnectedX, IconSearch, IconUserOff,
} from '@tabler/icons-react'
import { EmptyState, FilterChip, PageTitle } from '@lighthouse/design'
import { RestrictedState } from '../components/access/RestrictedState'
import { CartographerLink } from '../components/cartographer/CartographerLink'
import { EpisodeStateCell } from '../components/retirement/EpisodeStateCell'
import { RetirementEpisodes } from '../components/retirement/RetirementEpisodes'
import {
  REASON_FILTERS, RUN_COUNT_WITHHELD_HINT, STANDING_CAVEATS, SUPERSEDED_CAPTION,
  asReasonFilter, candidateErrorTitle, candidateHeadline, daysSinceText, emptyCandidatesText, episodeFor,
  handoffsFor, instantLabel, interactiveRunsHint, lastRunByText, lastRunText, ownerText, reasonLabel, runCountText,
  runCountsCaption, runLogBannerText, scheduledRunsHint, thresholdText, workspaceText,
} from '../components/retirement/retirementFormat'
import { useLighthouseAdmin } from '../hooks/useLighthouseAdmin'
import { adminPageMayFetch } from '../utils/adminGate'
import { useRetirementCandidates } from '../hooks/useRetirement'
import { useEpisodeStates } from '../hooks/useRetirementEpisodes'
import { isApiError, problemDetailText } from '../services/apiError'
import { MAX_EPISODE_REPORT_IDS } from '../types/retirementEpisodes'
import type { RetirementEpisodeStateInfo } from '../types/retirementEpisodes'
import type { RetirementCandidate, RetirementOverview, RetirementReasonFilter } from '../types/retirement'
import { NOT_MEASURED, errorKind, errorSummary, exactNumber } from '../utils/format'
import { reportDetailPath } from '../utils/reportDetailPath'

/**
 * Fifty, and tied to the episode endpoint's by-report cap on purpose: the episode
 * column asks for exactly this page's report ids, and the server refuses more than
 * fifty. A larger page would have to slice, and a sliced lookup reads the missing
 * reports as "never on the clock". (The candidate endpoint itself clamps at 200.)
 */
const PAGE_SIZE = MAX_EPISODE_REPORT_IDS

const SURFACE = 'Retirement Candidates'

/** The slice of the episode-states query the column reads. */
interface EpisodeColumn {
  data: RetirementEpisodeStateInfo[] | undefined
  isLoading: boolean
  isError: boolean
}

/**
 * Retirement Candidates (Drydock): reports that appear unused, over
 * `GET api/observatory/retirement` (LighthouseAdmin, read-only), with each flagged
 * report's retirement episode beside it and the episode list below.
 *
 * FOUR SIGNALS, NEVER A SCORE. Idle, orphaned, superseded and scheduled-but-not-
 * opened want different responses from different people, so each row carries its
 * signals as separate chips and each has its own hand-off. Favourites sit beside
 * them as the case against retiring.
 *
 * WHAT THE SIGNALS CANNOT SEE is stated on every visit, not buried: idle is "no
 * RECORDED run", and not opened in Cartographer is not unread.
 *
 * NO ACTION VERB (doc 17, A1, ruled as D6a). Every rung of the retirement ladder
 * is Cartographer's; this page observes and hands off, and the controller has no
 * mutating verb (a test pins that). A candidate's name opens its Report Detail on
 * the Governance tab (`/registry/:reportId?tab=governance`, D5), where its
 * retirement signals and episodes sit beside its ownership — a read, not a verb.
 *
 * THE EPISODE COLUMN NEVER BLOCKS THE LIST. Its endpoint depends on migration
 * AddRetirementCandidate, applied nowhere yet, so today it fails. The column then
 * shows dashes under a captioned header, and the Episodes section below explains;
 * the candidate list reads none of that table.
 */
export function RetirementPage() {
  const gate = useLighthouseAdmin()
  const denied = gate.state === 'denied'
  // Not on `pending` either: on a cold load the catalogue's answer is on its way, and asking first races it.
  const mayFetch = adminPageMayFetch(gate)

  // The signal is in the URL so a filtered view is a link somebody can send, and so
  // another surface can hand off to one signal. Checked, never trusted: a stale or
  // hand-typed value reads as "All" rather than as the server's 400.
  const [searchParams, setSearchParams] = useSearchParams()
  const reason = asReasonFilter(searchParams.get('reason'))

  // The page belongs to the filter it was chosen under, so a filter change (by chip,
  // or by Back) lands on page 1 without an effect.
  const [paging, setPaging] = useState<{ reason: RetirementReasonFilter | null; page: number }>({ reason, page: 1 })
  const page = paging.reason === reason ? paging.page : 1

  // Both hooks run unconditionally (hooks may not be conditional) and neither
  // fetches on `denied` or `pending`: the gate contract.
  const candidates = useRetirementCandidates(
    { skip: (page - 1) * PAGE_SIZE, top: PAGE_SIZE, reason },
    { enabled: mayFetch },
  )
  const reportIds = candidates.data?.candidates.map((c) => c.reportId) ?? []
  const episodeStates = useEpisodeStates(reportIds, { enabled: mayFetch })

  const chooseReason = (value: RetirementReasonFilter | null) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set('reason', value)
    else next.delete('reason')
    setSearchParams(next, { replace: true })
    setPaging({ reason: value, page: 1 })
  }

  return (
    <Stack gap="lg" pb="xl">
      <PageTitle
        subtitle="Drydock: reports that appear unused, the evidence behind each signal, and what has happened to each one flagged. Read-only; every action on a report is Cartographer's."
      >
        Retirement Candidates
      </PageTitle>

      {denied ? (
        <RestrictedState surface={SURFACE} reason={gate.reason} />
      ) : (
        <>
          {/*
            Chips, not a SegmentedControl: five labels need more than a phone's
            width, and a SegmentedControl cannot wrap, so it clipped them. Outside
            the data partition, so a filter that failed can be changed.
          */}
          <Stack gap={4}>
            <Group gap={6} role="group" aria-label="Filter by signal">
              {REASON_FILTERS.map(({ value, label }) => (
                <FilterChip key={value ?? 'all'} active={reason === value} onClick={() => chooseReason(value)}>
                  {label}
                </FilterChip>
              ))}
            </Group>
            <Text size="xs" c="dimmed">{SUPERSEDED_CAPTION}</Text>
          </Stack>

          {/*
            `|| !data` is load-bearing: between React Query's retries the flags read
            "not loading, not errored" with no payload, which would otherwise render
            as an empty list, the one reading this page must never fake.
          */}
          {candidates.isError ? (
            <CandidatesError error={candidates.error} />
          ) : candidates.isLoading || !candidates.data ? (
            <Center py="xl"><Loader /></Center>
          ) : (
            <CandidatesContent
              overview={candidates.data}
              reason={reason}
              page={page}
              onPage={(next) => setPaging({ reason, page: next })}
              episodes={{
                data: episodeStates.data,
                isLoading: episodeStates.isLoading,
                isError: episodeStates.isError,
              }}
            />
          )}

          {/* Handles its own gate, loading, 403, 500 (the unapplied migration) and empty. */}
          <RetirementEpisodes />
        </>
      )}
    </Stack>
  )
}

function CandidatesContent({
  overview, reason, page, onPage, episodes,
}: {
  overview: RetirementOverview
  reason: RetirementReasonFilter | null
  page: number
  onPage: (page: number) => void
  episodes: EpisodeColumn
}) {
  const banner = runLogBannerText(overview)
  const countsCaption = runCountsCaption(overview)
  const rows = overview.candidates

  return (
    <Stack gap="md">
      {banner && (
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light">
          <Text size="sm">
            {banner}. No candidate carries that signal, and the Scheduled deliveries and Interactive opens columns
            are withheld rather than counted from a partial record.
          </Text>
        </Alert>
      )}

      <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <Stack gap={6}>
          <Text fw={600}>{candidateHeadline(overview, reason)}</Text>
          <Text size="sm" c="dimmed">{thresholdText(overview)}</Text>
          {STANDING_CAVEATS.map((caveat) => (
            <Group key={caveat} gap={6} wrap="nowrap" align="flex-start">
              <IconInfoCircle size={14} style={{ flexShrink: 0, marginTop: 3, color: 'var(--color-text-disabled)' }} />
              <Text size="xs" c="dimmed">{caveat}</Text>
            </Group>
          ))}
          {countsCaption && (
            <Group gap={6} wrap="nowrap" align="flex-start" data-caption="run-counts">
              <IconInfoCircle size={14} style={{ flexShrink: 0, marginTop: 3, color: 'var(--color-text-disabled)' }} />
              <Text size="xs" c="dimmed">{countsCaption}</Text>
            </Group>
          )}
        </Stack>
      </Paper>

      <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        {rows.length === 0 ? (
          <EmptyCandidates overview={overview} reason={reason} />
        ) : (
          <>
            {/* Thirteen columns: the overflow is the table's own, never the page body's. */}
            <Table.ScrollContainer minWidth={1600}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Report</Table.Th>
                    <Table.Th>Workspace</Table.Th>
                    <Table.Th>Owner</Table.Th>
                    <Table.Th>Signals</Table.Th>
                    <Table.Th>Last run</Table.Th>
                    <Table.Th ta="right">Days since</Table.Th>
                    <Table.Th>Last run by</Table.Th>
                    <Table.Th ta="right">Active schedules</Table.Th>
                    <Table.Th ta="right">
                      <HeaderHint label="Favourites" hint="The case against retiring it: a durable 'this matters to me' that survives every prune" />
                    </Table.Th>
                    <Table.Th ta="right">
                      <HeaderHint label="Scheduled deliveries" hint={scheduledRunsHint(overview.unreadThresholdDays)} />
                    </Table.Th>
                    <Table.Th ta="right">
                      <HeaderHint label="Interactive opens" hint={interactiveRunsHint(overview.unreadThresholdDays)} />
                    </Table.Th>
                    <Table.Th>
                      {episodes.isError ? (
                        <Tooltip label="Episode data unavailable: the Episodes section below says why" multiline w={260}>
                          <Group gap={4} wrap="nowrap">
                            <span>Episode</span>
                            <IconAlertTriangle size={12} aria-label="Episode data unavailable" />
                          </Group>
                        </Tooltip>
                      ) : 'Episode'}
                    </Table.Th>
                    <Table.Th>Taken up by</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((candidate) => (
                    <CandidateRow key={candidate.reportId} candidate={candidate} episodes={episodes} />
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            <Group justify="space-between" mt="md">
              <Text size="sm" c="dimmed">
                Page {overview.pageNumber} of {Math.max(1, overview.totalPages)}
                {' · '}
                {exactNumber(overview.totalCount)} {overview.totalCount === 1 ? 'candidate' : 'candidates'}
              </Text>
              {overview.totalPages > 1 && (
                <Pagination value={page} total={overview.totalPages} onChange={onPage} />
              )}
            </Group>
          </>
        )}
      </Paper>
    </Stack>
  )
}

function CandidateRow({ candidate, episodes }: { candidate: RetirementCandidate; episodes: EpisodeColumn }) {
  const navigate = useNavigate()
  const owner = ownerText(candidate)
  const lastRunBy = lastRunByText(candidate)
  const lastRunInstant = instantLabel(candidate.lastRunAt)
  const detail = reportDetailPath(candidate.reportId, 'governance')

  return (
    <Table.Tr>
      <Table.Td>
        <Stack gap={2} align="flex-start">
          <Anchor
            component="button"
            type="button"
            size="sm"
            fw={500}
            ta="left"
            data-report-detail={detail}
            onClick={() => navigate(detail)}
          >
            {candidate.name}
          </Anchor>
          {candidate.replacementReportId != null && (
            <Text size="xs" c="dimmed">Replacement recorded: <Code>{candidate.replacementReportId}</Code></Text>
          )}
        </Stack>
      </Table.Td>

      <Table.Td>
        <Group gap={6} wrap="nowrap">
          <Text size="sm">{workspaceText(candidate)}</Text>
          {candidate.isPersonalWorkspace && (
            <Badge size="xs" variant="outline" color="gray">personal workspace</Badge>
          )}
        </Group>
      </Table.Td>

      <Table.Td>
        {owner.tooltip ? (
          <Tooltip label={owner.tooltip} multiline w={320}>
            <Text size="sm" c={owner.text === NOT_MEASURED ? 'dimmed' : undefined}>{owner.text}</Text>
          </Tooltip>
        ) : (
          <Text size="sm">{owner.text}</Text>
        )}
      </Table.Td>

      <Table.Td>
        {/* Separate chips, neutral: a signal is evidence, not a verdict, and never a score. */}
        <Group gap={4}>
          {candidate.reasons.map((r) => (
            <Badge key={r} size="sm" variant="light" color="gray" style={{ textTransform: 'none' }}>
              {reasonLabel(r)}
            </Badge>
          ))}
        </Group>
      </Table.Td>

      <Table.Td>
        {lastRunInstant ? (
          <Tooltip label={lastRunInstant}>
            <Text size="sm">{lastRunText(candidate.lastRunAt)}</Text>
          </Tooltip>
        ) : (
          <Text size="sm">{lastRunText(candidate.lastRunAt)}</Text>
        )}
      </Table.Td>

      <Table.Td ta="right">
        <Text size="sm" c={candidate.daysSinceLastRun == null ? 'dimmed' : undefined}>
          {daysSinceText(candidate.daysSinceLastRun)}
        </Text>
      </Table.Td>

      <Table.Td>
        {lastRunBy.tooltip ? (
          <Tooltip label={lastRunBy.tooltip} multiline w={320}>
            <Text size="sm" c="dimmed">{lastRunBy.text}</Text>
          </Tooltip>
        ) : (
          <Text size="sm" c={lastRunBy.text === NOT_MEASURED ? 'dimmed' : undefined}>{lastRunBy.text}</Text>
        )}
      </Table.Td>

      <Table.Td ta="right"><Text size="sm">{exactNumber(candidate.activeScheduleCount)}</Text></Table.Td>
      <Table.Td ta="right"><Text size="sm">{exactNumber(candidate.favoriteCount)}</Text></Table.Td>
      <Table.Td ta="right"><RunCount value={candidate.scheduledDeliveries} /></Table.Td>
      <Table.Td ta="right">
        <RunCount
          value={candidate.interactiveOpens}
          hint={candidate.interactiveOpensRecorded === false ? OPENS_NOT_RECORDED_HINT : undefined}
        />
      </Table.Td>

      <Table.Td>
        {episodes.isError ? (
          <Text size="sm" c="dimmed">{NOT_MEASURED}</Text>
        ) : episodes.isLoading ? (
          <MSkeleton height={12} width={90} aria-label="Loading episode" />
        ) : (
          <EpisodeStateCell state={episodeFor(episodes.data, candidate.reportId)} />
        )}
      </Table.Td>

      <Table.Td>
        <Handoffs reasons={candidate.reasons} reportId={candidate.reportId} />
      </Table.Td>
    </Table.Tr>
  )
}

/** A run count inside the unread window: the number, or a dash that says why. */
/** Why a report's opens read as not measured even where the log covers the window. */
const OPENS_NOT_RECORDED_HINT =
  'Opens of this report are not counted across this window: it declares no parameters, and before every open of a '
  + 'saved report was recorded Spyglass opened such a report with an unsaved query that wrote no run. "Never opened" '
  + 'is not a reading the log can make yet, so the unread signal is not evaluated for it.'

function RunCount({ value, hint }: { value: number | null; hint?: string }) {
  if (value == null) {
    return (
      <Tooltip label={hint ?? RUN_COUNT_WITHHELD_HINT} multiline w={280}>
        <Text size="sm" c="dimmed">{runCountText(value)}</Text>
      </Tooltip>
    )
  }
  return <Text size="sm">{runCountText(value)}</Text>
}

/** Where each of the row's signals is taken up. Nouns, never verbs: nothing is done here. */
function Handoffs({ reasons, reportId }: { reasons: string[]; reportId: string }) {
  const navigate = useNavigate()
  const handoffs = handoffsFor(reasons, reportId)

  if (handoffs.length === 0) {
    return <Text size="sm" c="dimmed">{NOT_MEASURED}</Text>
  }

  return (
    <Stack gap={2}>
      {handoffs.map((h) => {
        const key = `${h.signal}:${h.kind}`
        if (h.kind === 'route') {
          return (
            <Anchor key={key} component="button" type="button" size="sm" ta="left" onClick={() => navigate(h.to)}>
              {h.label}
            </Anchor>
          )
        }
        if (h.kind === 'cartographer') {
          return <span key={key}><CartographerLink path={h.path} label={h.label} /></span>
        }
        return <Text key={key} size="sm" c="dimmed">{reasonLabel(h.signal)}: {h.text}</Text>
      })}
    </Stack>
  )
}

function HeaderHint({ label, hint }: { label: string; hint: string }) {
  return (
    <Tooltip label={hint} multiline w={280}>
      <span style={{ textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>{label}</span>
    </Tooltip>
  )
}

function EmptyCandidates({ overview, reason }: { overview: RetirementOverview; reason: RetirementReasonFilter | null }) {
  const { title, message } = emptyCandidatesText(overview, reason)
  return (
    <EmptyState
      icon={reason == null ? <IconInfoCircle size={28} stroke={1.5} /> : <IconSearch size={28} stroke={1.5} />}
      title={title}
      message={message}
    />
  )
}

/**
 * The list failed. A 403 is the missing role (neutral, RestrictedState); anything
 * else is a fault, and says that nothing is known, which is not "no candidates".
 */
function CandidatesError({ error }: { error: unknown }) {
  const kind = errorKind(error)

  if (kind === 'forbidden') {
    return <RestrictedState surface={SURFACE} reason={isApiError(error) ? problemDetailText(error.body) : null} />
  }

  const summary = errorSummary(error)
  const icon = kind === 'unauthorized'
    ? <IconUserOff size={28} stroke={1.5} />
    : kind === 'network'
      ? <IconPlugConnectedX size={28} stroke={1.5} />
      : <IconAlertCircle size={28} stroke={1.5} />

  return (
    <EmptyState
      icon={icon}
      title={candidateErrorTitle(kind)}
      message="The request failed, so nothing is known here about any report. That is not the same as there being no candidates."
      action={summary ? <Code style={{ overflowWrap: 'anywhere' }}>{summary}</Code> : undefined}
    />
  )
}
